import { useState } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { PhoneCall, Mail, MapPin } from 'lucide-react';
import { FaInstagram, FaFacebook, FaYoutube, FaWhatsapp } from 'react-icons/fa';
import { WHATSAPP_NUMBER, CONTACT_EMAIL, CONTACT_PHONE } from '@/lib/index';
import { supabase } from '@/integrations/supabase/client';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export default function Contact() {
  const { toast } = useToast();
  const [formState, setFormState] = useState<FormState>('idle');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    interest: 'none',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('submitting');

    try {
      const { data, error } = await supabase.functions.invoke('contact-handler-ave', {
        body: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          interest: formData.interest === 'none' ? 'General Inquiry' : formData.interest,
          message: formData.message,
        },
      });

      if (error) throw error;

      setFormState('success');
      toast({
        title: 'Message Sent Successfully',
        description: 'Thank you for contacting us. We will get back to you soon.',
      });

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        interest: 'none',
        message: '',
      });
    } catch (error) {
      setFormState('error');
      toast({
        title: 'Error Sending Message',
        description: 'Something went wrong. Please try again or contact us directly.',
        variant: 'destructive',
      });
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}`, '_blank');
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background py-24">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Get in Touch
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Ready to invest in Zanzibar's luxury real estate? Contact us today.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-semibold text-foreground mb-6">Contact Information</h2>
                <div className="space-y-4">
                  <Card className="bg-card border-border hover:border-accent/50 transition-colors">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <PhoneCall className="w-6 h-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                        <p className="text-muted-foreground">{CONTACT_PHONE}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border hover:border-accent/50 transition-colors">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <Mail className="w-6 h-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">Email</h3>
                        <p className="text-muted-foreground">{CONTACT_EMAIL}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-card border-border hover:border-accent/50 transition-colors">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-primary/10">
                        <MapPin className="w-6 h-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">Location</h3>
                        <p className="text-muted-foreground">Paje, Zanzibar, Tanzania</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div>
                <Button
                  onClick={handleWhatsApp}
                  className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold py-6 text-lg"
                >
                  <FaWhatsapp className="w-6 h-6 mr-2" />
                  Chat on WhatsApp
                </Button>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-foreground mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-lg bg-card border border-border hover:border-accent hover:bg-primary/5 transition-all"
                  >
                    <FaInstagram className="w-6 h-6 text-foreground" />
                  </a>
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-lg bg-card border border-border hover:border-accent hover:bg-primary/5 transition-all"
                  >
                    <FaFacebook className="w-6 h-6 text-foreground" />
                  </a>
                  <a
                    href="https://youtube.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-lg bg-card border border-border hover:border-accent hover:bg-primary/5 transition-all"
                  >
                    <FaYoutube className="w-6 h-6 text-foreground" />
                  </a>
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-lg bg-card border border-border hover:border-accent hover:bg-primary/5 transition-all"
                  >
                    <FaWhatsapp className="w-6 h-6 text-foreground" />
                  </a>
                </div>
              </div>
            </div>

            <div>
              <Card className="bg-card border-border">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-6">Send Us a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-foreground mb-2">
                          First Name
                        </label>
                        <Input
                          id="firstName"
                          type="text"
                          value={formData.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                          required
                          disabled={formState === 'submitting'}
                          className="bg-background border-border"
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-foreground mb-2">
                          Last Name
                        </label>
                        <Input
                          id="lastName"
                          type="text"
                          value={formData.lastName}
                          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                          required
                          disabled={formState === 'submitting'}
                          className="bg-background border-border"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                        Email
                      </label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        disabled={formState === 'submitting'}
                        className="bg-background border-border"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                        Phone
                      </label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        disabled={formState === 'submitting'}
                        className="bg-background border-border"
                      />
                    </div>

                    <div>
                      <label htmlFor="interest" className="block text-sm font-medium text-foreground mb-2">
                        Interest
                      </label>
                      <Select
                        value={formData.interest}
                        onValueChange={(value) => setFormData({ ...formData, interest: value })}
                        disabled={formState === 'submitting'}
                      >
                        <SelectTrigger className="bg-background border-border">
                          <SelectValue placeholder="Select your interest" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select your interest</SelectItem>
                          <SelectItem value="Plot Purchase">Plot Purchase</SelectItem>
                          <SelectItem value="Villa Purchase">Villa Purchase</SelectItem>
                          <SelectItem value="Condominium">Condominium</SelectItem>
                          <SelectItem value="Development Project">Development Project</SelectItem>
                          <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                        Message
                      </label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        disabled={formState === 'submitting'}
                        rows={6}
                        className="bg-background border-border resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={formState === 'submitting'}
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-6 text-lg"
                    >
                      {formState === 'submitting' ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
